import { FLOW_VERSION } from './flowConfig';
import type { AlmaFlowAnswers, FlowLists } from './types';
import { flowAnswers } from './flow';
import type { FlowState } from './flow';

/**
 * The two-phase flow's draft, beside the chat transcript draft
 * (TRY_CHAT_STORAGE_KEY) so a reload or the Google OAuth redirect resumes at
 * the same step. Lists aren't saved — they're reloaded, and saved answers keep
 * their own labels if an option was renamed or deactivated meanwhile.
 */
export const TRY_CHAT_FLOW_STORAGE_KEY = 'sekretly_try_chat_flow';

export type FlowDraft = Omit<FlowState, 'lists'>;

export function saveFlowDraft(state: FlowState): void {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { lists, ...draft } = state;
  try {
    localStorage.setItem(TRY_CHAT_FLOW_STORAGE_KEY, JSON.stringify(draft));
  } catch { /* storage full or blocked — the flow still works, it just won't resume */ }
}

/** The saved draft, or null if there is none or it's from another flow version. */
export function readFlowDraft(): FlowDraft | null {
  try {
    const raw = localStorage.getItem(TRY_CHAT_FLOW_STORAGE_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as FlowDraft;
    return draft?.version === FLOW_VERSION && typeof draft.step === 'string' ? draft : null;
  } catch {
    return null;
  }
}

export function clearFlowDraft(): void {
  try {
    localStorage.removeItem(TRY_CHAT_FLOW_STORAGE_KEY);
  } catch { /* nothing to clear */ }
}

/** A saved draft with freshly loaded lists. Pass through flowReducer's `lists` action to drop vanished picks. */
export function hydrateFlowDraft(draft: FlowDraft, lists: FlowLists): FlowState {
  return { ...draft, recapIds: draft.recapIds ?? {}, phase2StartedAt: draft.phase2StartedAt ?? null, p2Questions: draft.p2Questions ?? 0, completedAt: draft.completedAt ?? null, lists };
}

const NO_LISTS: FlowLists = { cities: [], specialties: [], branches: [], walking: [], failed: [] };

/** The saved draft's finished Phase 1 answers, or null (no draft, classic journey, or Phase 1 not done). */
export function readDraftAnswers(): AlmaFlowAnswers | null {
  const draft = readFlowDraft();
  return draft ? flowAnswers(hydrateFlowDraft(draft, NO_LISTS)) : null;
}
